import { createContext, FC, ReactNode, useContext, useMemo } from 'react'

import { HEALTH_ONTOLOGY } from './healthOntology'
import {
  EnrichedOntologyFieldDefinition,
  OntologyField,
  OntologyRolodexEntryType,
  OntologyV2,
} from './ontologyTypes'
import {
  bucketLinkedTypes,
  deriveIdFields,
  findLowestType,
  getInflectionType,
  isDescendant,
  mergeOntologies,
  toTitleCase,
  validateMergedOntology,
} from './ontologyUtils'

export interface OntologyContextType {
  ontology: OntologyV2
  /**
   * @deprecated Use `ontology.types[typeName].fields` instead.
   * This flattened field map will be removed in a future version.
   */
  fields: Readonly<Record<OntologyField, EnrichedOntologyFieldDefinition>>
  getInflectionType: (
    branchOne: ReadonlyArray<OntologyRolodexEntryType>,
    branchTwo: ReadonlyArray<OntologyRolodexEntryType>
  ) => OntologyRolodexEntryType | null
  bucketLinkedTypes: (
    types: ReadonlyArray<OntologyRolodexEntryType>
  ) => ReadonlyArray<ReadonlyArray<OntologyRolodexEntryType>>
  isDescendant: (
    type: OntologyRolodexEntryType,
    ancestor: OntologyRolodexEntryType
  ) => boolean
  findLowestType: (
    types: ReadonlyArray<OntologyRolodexEntryType>
  ) => OntologyRolodexEntryType | null
  entryTypeNames: Readonly<Record<OntologyRolodexEntryType, string>>
}

/**
 * Helper function to extract out the type and field from a FilterField.
 * @param field the field
 * @returns a tuple of ['entry-type', 'key']
 */
function getTypeAndKey(field: OntologyField): [string, string] {
  const split = field.split('.')
  return [split[0], field.substring(split[0].length + 1)]
}

function createOntologyContext(schema: OntologyV2): OntologyContextType {
  const schemaEntries = Object.entries(schema.types)

  const tagGraph = Object.fromEntries(
    schemaEntries.map(([type, typeDef]) => [type, typeDef.requiredTags])
  ) as Record<string, ReadonlyArray<string>>

  // @deprecated - We flatten fields with the type prefix for backward compatibility.
  // Use ontology.types[typeName].fields instead.
  const fields: Record<OntologyField, EnrichedOntologyFieldDefinition> =
    Object.fromEntries(
      schemaEntries.flatMap(([typeName, typeDef]) =>
        Object.entries(typeDef.fields).map(([fieldPath, fieldDef]) => {
          const fullKey = `${typeName}.${fieldPath}` as OntologyField
          const [entryType, fieldKey] = getTypeAndKey(fullKey)
          const keyOrField =
            fieldKey.startsWith('id') ||
            fieldKey.startsWith('tags') ||
            fieldKey.startsWith('data')
              ? 'field'
              : 'key'
          return [
            fullKey,
            {
              name: fieldDef.name,
              type: fieldDef.type,
              key: fieldKey,
              entryType,
              keyOrField,
              allowedValues: fieldDef.allowedValues,
            },
          ]
        })
      )
    )

  validateMergedOntology(schema)

  return {
    ontology: schema,
    fields,
    getInflectionType: (branchOne, branchTwo) =>
      getInflectionType(branchOne, branchTwo, tagGraph),
    bucketLinkedTypes: (types) => bucketLinkedTypes(types, tagGraph),
    isDescendant: (type, ancestor) => isDescendant(type, ancestor, tagGraph),
    findLowestType: (types) => findLowestType(types, tagGraph),
    entryTypeNames: Object.fromEntries(
      Object.entries(schema.types).map(([type, typeDef]) => [
        type,
        typeDef.displayName ?? toTitleCase(type),
      ])
    ),
  }
}

export const HEALTH_ONTOLOGY_CONTEXT: OntologyContextType =
  createOntologyContext(HEALTH_ONTOLOGY)

const OntologyContext = createContext<OntologyContextType | undefined>(
  // TODO this should be switched to undefined to force consumers to create an ontology provider in their apps.
  // Otherwise, we risk ignoring org-specific ontologies for apps that don't create a provider.
  // https://app.asana.com/1/957917091101853/project/1209606087561609/task/1211435751461702
  HEALTH_ONTOLOGY_CONTEXT
)

export function useOntology(): OntologyContextType {
  const context = useContext(OntologyContext)
  if (context === undefined) {
    throw new Error('useOntology must be called within an OntologyProvider')
  }
  return context
}

export const OntologyProvider: FC<{
  ontologyAdditions: ReadonlyArray<OntologyV2> | undefined
  children: ReactNode
}> = ({ ontologyAdditions, children }) => {
  const mergedOntology = useMemo(
    () => mergeOntologies(HEALTH_ONTOLOGY, ...(ontologyAdditions ?? [])),

    [ontologyAdditions]
  )

  const ontologyWithIdFields = useMemo(
    () => deriveIdFields(mergedOntology),
    [mergedOntology]
  )

  const contextValue = useMemo(
    () => createOntologyContext(ontologyWithIdFields),
    [ontologyWithIdFields]
  )

  return (
    <OntologyContext.Provider value={contextValue}>
      {children}
    </OntologyContext.Provider>
  )
}
